import { useTranslation } from 'react-i18next';
import { Truck } from 'lucide-react';
import { Button } from '../ui/Button';
import { touch } from './Modal';
import type { VanBoard, VanSummary } from '../../lib/receiving';

/**
 * Receiving by van (spec §5, migration 133). No van in progress: "Which van are you unloading?",
 * one button per supplier with what it owes today. A van in progress: its supplier, "x / y units
 * expected from this supplier today" and End of van. A van just ended: its summary.
 *
 * Suppliers are shown by CODE — on prod two of them are both named "Entrepôt 1".
 */
export function VanPanel({
  board,
  ended,
  busy,
  onStart,
  onEnd,
  onGoClose,
}: {
  board: VanBoard | null;
  ended: VanSummary | null;
  busy: boolean;
  onStart: (supplierId: string) => void;
  onEnd: () => void;
  onGoClose: () => void;
}) {
  const { t } = useTranslation();
  if (!board) return null;
  const van = board.current;

  if (van) {
    const got = van.expectedToday - van.stillExpected;
    const pct = van.expectedToday ? Math.round((got / van.expectedToday) * 100) : 100;
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-card px-4 py-3">
        <div>
          <div className="text-xs text-muted-foreground">{t('rcv.van.inProgress')}</div>
          <div className="flex items-center gap-2 text-2xl font-bold text-primary">
            <Truck className="h-6 w-6" /> {van.supplierCode}
          </div>
        </div>
        <div className="min-w-[14rem] flex-1 text-sm">
          {t('rcv.van.progress', { got, expected: van.expectedToday })}
          {' · '}
          {van.stillExpected ? t('rcv.van.toGo', { count: van.stillExpected }) : t('rcv.van.allIn')}
          <div className="mt-1 h-2 overflow-hidden rounded bg-muted">
            <div className="h-full bg-green-600" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <Button className={touch} disabled={busy} onClick={onEnd}>
          {t('rcv.van.end')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {ended && (
        <div className="rounded-xl border bg-card px-4 py-3">
          <div className="text-xl font-semibold">
            {t('rcv.van.finished', { supplier: ended.supplierCode })}
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2 text-center">
            {(
              [
                ['units', ended.units],
                ['toCustomers', ended.toCustomers],
                ['toStock', ended.toShelf],
                ['toQuarantine', ended.toQuarantine],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="rounded-lg border bg-muted/40 px-2 py-2">
                <div className="text-2xl font-bold tabular-nums">{v}</div>
                <div className="text-xs text-muted-foreground">{t(`rcv.stats.${k}`)}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm">
            {ended.stillExpected
              ? t('rcv.van.notDelivered', { count: ended.stillExpected })
              : t('rcv.van.everythingArrived')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('rcv.van.finishedBy', { name: ended.startedBy })}
          </p>
        </div>
      )}
      <div className="rounded-xl border bg-card px-4 py-3">
        <h2 className="text-xl font-semibold">{t('rcv.van.which')}</h2>
        <p className="text-sm text-muted-foreground">{t('rcv.van.whichHint')}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {board.suppliers.map((s) => {
            const done = s.expectedToday > 0 && s.stillExpected === 0;
            return (
              <button
                key={s.supplierId}
                type="button"
                disabled={busy}
                onClick={() => onStart(s.supplierId)}
                className={`flex min-h-[96px] flex-col items-center justify-center gap-1 rounded-xl border px-3 text-center hover:border-primary disabled:opacity-50 ${done ? 'border-green-600 bg-green-50 dark:bg-green-950/30' : ''}`}
              >
                <span className="text-xl font-semibold">{s.code}</span>
                <span className="text-xs text-muted-foreground">{s.name}</span>
                <span className="text-sm text-muted-foreground">
                  {s.expectedToday
                    ? t('rcv.van.receivedOf', { got: s.received, expected: s.expectedToday })
                    : t('rcv.van.nothingToday')}
                  {done ? ` · ${t('rcv.van.done')}` : ''}
                </span>
              </button>
            );
          })}
        </div>
        {!board.dayClosed && (board.suppliers.some((s) => s.vansToday > 0) || ended) && (
          <p className="mt-3 text-sm">
            {t('rcv.van.allVansDone')}{' '}
            <Button variant="outline" className="ml-1 min-h-12" onClick={onGoClose}>
              {t('rcv.van.goClose')}
            </Button>
          </p>
        )}
      </div>
    </div>
  );
}
